import { CardMedia, Stack, Typography, RadioGroup, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useField, useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useRefetchableFragment,
} from 'react-relay';
import { useDebouncedCallback } from 'use-debounce';

import { RadioCardOption } from '@woovi/form';
import { useRefetchTransition } from '@woovi/hooks';
import {
  type FetchKeyProp,
  withRelayBoundary,
  type ExtractNode,
} from '@woovi/relay';

import type { PaymentActiveITPBanksList_charge$key } from './__generated__/PaymentActiveITPBanksList_charge.graphql.ts';
import type {
  PaymentActiveITPBanksList_query$data,
  PaymentActiveITPBanksList_query$key,
} from './__generated__/PaymentActiveITPBanksList_query.graphql.ts';
import type { PaymentActiveITPBanksListQuery } from './__generated__/PaymentActiveITPBanksListQuery.graphql.ts';
import type { PaymentActiveITPBanksListRefetchQuery } from './__generated__/PaymentActiveITPBanksListRefetchQuery.graphql.ts';

import type { ReactNode } from 'react';

type PaymentActiveITPBanksListProps = {
  charge: PaymentActiveITPBanksList_charge$key;
  isPending: boolean;
} & FetchKeyProp;

const PaymentActiveITPBanksList = ({
  fetchKey,
  isPending,
  ...props
}: PaymentActiveITPBanksListProps): ReactNode => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { values } = useFormikContext();
  const [field, , helpers] = useField('bank');

  const charge = useFragment<PaymentActiveITPBanksList_charge$key>(
    graphql`
      fragment PaymentActiveITPBanksList_charge on Charge {
        companyBankAccount {
          itpCode
        }
      }
    `,
    props.charge,
  );

  const response = useLazyLoadQuery<PaymentActiveITPBanksListQuery>(
    graphql`
      query PaymentActiveITPBanksListQuery($itpCode: String, $search: String) {
        ...PaymentActiveITPBanksList_query
          @arguments(itpCode: $itpCode, search: $search)
      }
    `,
    {
      itpCode: charge.companyBankAccount?.itpCode,
      search: values.search,
    },
    {
      fetchKey,
    },
  );

  const [query, refetchFn] = useRefetchableFragment<
    PaymentActiveITPBanksListRefetchQuery,
    PaymentActiveITPBanksList_query$key
  >(
    graphql`
      fragment PaymentActiveITPBanksList_query on Query
      @refetchable(queryName: "PaymentActiveITPBanksListRefetchQuery")
      @argumentDefinitions(
        itpCode: { type: String }
        search: { type: String }
      ) {
        itpBanks(itpCode: $itpCode, search: $search) {
          edges {
            node {
              providerId
              providerParticipantId
              name
              logo
            }
          }
        }
      }
    `,
    response,
  );

  const [, refetch] = useRefetchTransition(refetchFn);

  const handleDebounceApplied = useDebouncedCallback(() => {
    refetch({
      itpCode: charge.companyBankAccount?.itpCode,
      search: values.search,
    });
  }, 3000);

  useEffect(() => {
    handleDebounceApplied();
  }, [values.search]);

  const handleClickRadio = (
    bank: ExtractNode<PaymentActiveITPBanksList_query$data['itpBanks']>,
  ) => {
    helpers.setValue({
      providerBankId: bank?.providerId,
      providerParticipantId: bank?.providerParticipantId,
    });
  };

  if (!query.itpBanks?.edges.length) {
    return <Typography>{t('No results found')}</Typography>;
  }

  return (
    <Box
      sx={{ maxHeight: theme.spacing(64), overflowY: 'auto', width: '100%' }}
    >
      <RadioGroup
        value={field.value?.providerBankId || ''}
        sx={{ width: '100%' }}
      >
        <Stack spacing={2} width='100%'>
          {query.itpBanks.edges.map((bank) => (
            <RadioCardOption
              key={bank?.node?.providerId}
              value={bank?.node?.providerId}
              disabled={isPending}
              handleClickRadio={() => handleClickRadio(bank?.node)}
              label={
                <Stack
                  spacing={2}
                  direction='row'
                  sx={{
                    alignItems: 'center',
                    minHeight: theme.spacing(9),
                    marginLeft: 2,
                  }}
                >
                  <CardMedia
                    component='img'
                    sx={{
                      maxWidth: theme.spacing(6),
                      objectFit: 'contain',
                    }}
                    image={bank?.node?.logo}
                  />
                  <Typography
                    variant='h6'
                    fontWeight={theme.typography.fontWeightBold}
                  >
                    {bank?.node?.name}
                  </Typography>
                </Stack>
              }
            />
          ))}
        </Stack>
      </RadioGroup>
    </Box>
  );
};

export default withRelayBoundary(PaymentActiveITPBanksList);
